/// <reference path="../_references.ts"/>
module App.Login {
	import LocationService = App.Helpers.LocationService;
	import ToastService = App.Utils.ToastService;

	interface tokenData {
		token: string;
		user: userLoginData;
	}

	export interface roleLoginData {
		id: number;
		name: string;
	}

	export interface userLoginData {
		id: number;
		permissions: string[];
		role: roleLoginData|null;
		last_name?: string;
		tech_role: roleLoginData|null;
	}

	interface LocalStorage extends ng.local.storage.ILocalStorageService {
		authenticationToken: string;
		userData: userLoginData;
		old_data: LocalStorageBackup,
		$default: (settings: {[key: string] : any}) => void;
		$reset: () => void;
	}

	interface LocalStorageBackup {
		user_id: number;
		info: LocalStorage;
	}

	interface IErrorMessage {
		message?: string;
	}

	const LOGIN_ERROR = 'Unable to login: ',
		DEFAULT_ERROR = 'Unknown Error';

	export class LoginService {
		static $inject = ['$http', '$q', '$rootScope', '$window', '$localStorage', '$cookies', 'LocationService', 'ToastService'];
		private userData: userLoginData;

		constructor(
			private $http: ng.IHttpService,
			private $q: ng.IQService,
			private $rootScope: ng.IRootScopeService,
			private $window: Window,
			private $localStorage: LocalStorage,
			private $cookies: ng.cookies.ICookiesService,
			private LocationService: LocationService,
			private ToastService: ToastService
		) { }

		private getDomainObject() {
			let expireDate = new Date();

			expireDate.setDate(expireDate.getDate() + 1);
			return {
				path: '/',
				domain: cookieDomain,
				expires: expireDate,
			};
		}

		private updateToken(token?: string) {
			token = token || this.giveToken();
			if (token) {
				this.$cookies.put('authenticationToken', token, this.getDomainObject());
			}
		}

		isActiveSession() {
			this.updateToken();

			return angular.isDefined(this.$cookies.get('authenticationToken'));
		}

		createSession(tokenData: tokenData) {
			if (!tokenData.user.tech_role) {
				this.ToastService.show(LOGIN_ERROR + 'Not a technician');
				this.logoutSession();
				return;
			}

			this.updateToken(tokenData.token);
			if (this.$localStorage.old_data && tokenData.user && this.$localStorage.old_data.user_id === tokenData.user.id) {
				Object.keys(this.$localStorage.old_data.info).forEach(k => {
					this.$localStorage[k] = this.$localStorage.old_data.info[k];
				});
				delete this.$localStorage.old_data;
			}
			this.$localStorage.userData = this.userData = tokenData.user;
			this.$rootScope.$broadcast('sessionChange');
		}

		destroySession() {
			const data: LocalStorageBackup = {
				user_id: 0,
				info: angular.copy(this.$localStorage),
			};
			if (this.$localStorage.userData) {
				delete this.$localStorage.old_data;

				data.user_id = this.$localStorage.userData.id;
			}
			this.$localStorage.$reset();
			if (data.user_id) {
				this.$localStorage.old_data = data;
			}
			this.$cookies.remove('authenticationToken', {
				path: '/',
				domain: cookieDomain
			});
			this.$rootScope.$broadcast('sessionChange');
		}

		logoutSession(): ng.IPromise<{}> {
			const vm = this,
				cookie = vm.giveToken();

			vm.destroySession();

			if (cookie) {
				return vm.LocationService.getLocation().then((coords) => {
					return vm.$http(<ng.IRequestConfig>{
						method: 'DELETE',
						headers: {
							'API-Key': cookie
						},
						dataType: 'json',
						url: `${api}v1/logout`,
						data: {
							latitude: coords.latitude,
							longitude: coords.longitude
						}
					});
				});
			} else {
				return vm.$q.when({});
			}
		}

		giveToken() {
			return this.$cookies.get('authenticationToken');
		}

		giveUserData(): userLoginData {
			this.updateToken();

			return angular.copy(this.userData || (this.userData = this.$localStorage.userData));
		}

		authentication(username: string, password: string): ng.IPromise<tokenData|null> {
			return this.LocationService.getLocation().then((coords) => {
				return this.$http(<ng.IRequestConfig>{
					method: 'POST',
					dataType: 'json',
					url: `${api}v1/tokens`,
					headers: {
						username: username,
						password: password
					},
					data: {
						latitude: coords.latitude,
						longitude: coords.longitude
					}
				}).catch((err: ng.IHttpResponse<IErrorMessage>) => {
					this.ToastService.show(LOGIN_ERROR + (
						err && err.data && err.data.message || DEFAULT_ERROR
					));

					return null;
				});
			}).then((tokenData: ng.IHttpResponse<tokenData>) => {
				if (!tokenData) {
					this.destroySession();
					return null;
				}
				this.createSession(tokenData.data);
				return tokenData.data;
			});
		}
	}

	angular.module('tradeServe.utils.login', [
		'ngStorage',
		'helpers.locationService'
	]).service('LoginService', LoginService)
	// TODO: Change this to proper TypeScript.  I couldn't figure out exactly how, and I didn't want to wrestle with it for hours.
	.provider('LoginServiceProvider', [function() {
		var that = this;

		/**
		 * Eslint has issues with this.$get
		 * Set this to that, and set $get from that in an inline function
		 */
		(function() {
			that.$get = [
				'LoginService', (function() {
					return function(loader: any) {
						return loader.load();
					};
				})()
			];
		})();

		return this;
	}]);
}